'use client';

import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { PathBuilder } from '../partitioning/PathBuilder';
import {
  parseSamplePath,
  validateSegments,
} from '../partitioning/segmentHelpers';

export function PartitioningStep(
  props: StepProps<IngestionPipelineWizardState>
) {
  const { partitionSpec, partitionBuilderMode, samplePath } = props.wizardState;
  const { stepperLabels } = props;
  const { separator, segments } = partitionSpec;

  const [localSamplePath, setLocalSamplePath] = useState(samplePath);
  const [hasInteracted, setHasInteracted] = useState(segments.length > 0);

  const validationError = validateSegments(segments);

  const canGoNext = () => validationError === null;

  const handleSeparatorChange = (newSeparator: string) => {
    props.setWizardState((s) => ({
      ...s,
      partitionSpec: { ...s.partitionSpec, separator: newSeparator },
    }));
  };

  const handleModeChange = (newMode: 'build' | 'paste') => {
    props.setWizardState((s) => ({ ...s, partitionBuilderMode: newMode }));
  };

  const handleSegmentsChange = (
    newSegments: IngestionPipelineWizardState['partitionSpec']['segments']
  ) => {
    setHasInteracted(true);
    props.setWizardState((s) => ({
      ...s,
      partitionSpec: { ...s.partitionSpec, segments: newSegments },
    }));
  };

  const handleParsePath = () => {
    setHasInteracted(true);
    const parsed = parseSamplePath(localSamplePath, separator);
    props.setWizardState((s) => ({
      ...s,
      samplePath: localSamplePath,
      partitionSpec: { ...s.partitionSpec, segments: parsed },
    }));
  };

  const handleSamplePathKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      handleParsePath();
    }
  };

  return (
    <StepContainer
      stepperIndex={2}
      stepperLabels={stepperLabels}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
    >
      <Stack gap='24px'>
        {/* Separator */}
        <FormControl>
          <FormLabel>Path Separator</FormLabel>
          <TextField
            size='small'
            value={separator}
            onChange={(e) => handleSeparatorChange(e.target.value)}
            sx={{ maxWidth: '120px' }}
            inputProps={{ 'aria-label': 'path separator' }}
          />
        </FormControl>

        {/* Mode toggle */}
        <Stack gap='8px'>
          <FormLabel>Build Mode</FormLabel>
          <Stack direction='row' gap='12px'>
            {(
              [
                {
                  mode: 'paste' as const,
                  title: 'Paste a path',
                  caption: 'Auto-detect segments from a sample path',
                },
                {
                  mode: 'build' as const,
                  title: 'Build manually',
                  caption: 'Add and configure each segment by hand',
                },
              ] as const
            ).map(({ mode, title, caption }) => {
              const isSelected = partitionBuilderMode === mode;
              return (
                <Box
                  key={mode}
                  onClick={() => handleModeChange(mode)}
                  sx={{
                    flex: 1,
                    maxWidth: '300px',
                    border: '2px solid',
                    borderColor: isSelected
                      ? 'primary.main'
                      : 'var(--mui-palette-divider)',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    bgcolor: isSelected
                      ? 'action.selected'
                      : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    color={isSelected ? 'primary.main' : 'text.primary'}
                  >
                    {title}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {caption}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Stack>

        {/* Paste mode controls */}
        {partitionBuilderMode === 'paste' && (
          <Stack gap='12px' sx={{ width: '80%' }}>
            <FormControl>
              <FormLabel>Sample Path</FormLabel>
              <TextField
                size='small'
                placeholder={`e.g. data${separator}year=2024${separator}month=01${separator}file.json`}
                value={localSamplePath}
                onChange={(e) => setLocalSamplePath(e.target.value)}
                onKeyDown={handleSamplePathKeyDown}
                inputProps={{ 'aria-label': 'sample path' }}
              />
            </FormControl>
            <Button
              variant='outlined'
              size='small'
              onClick={handleParsePath}
              disabled={localSamplePath.trim() === ''}
              sx={{
                whiteSpace: 'nowrap',
                height: '40px',
                alignSelf: 'flex-start',
              }}
            >
              Parse Path
            </Button>
          </Stack>
        )}

        {/* Path Builder — unified segment strip + config panel */}
        <Stack gap='4px'>
          <FormLabel>Path Pattern</FormLabel>
          <PathBuilder
            segments={segments}
            separator={separator}
            showAddButton={partitionBuilderMode === 'build'}
            onChange={handleSegmentsChange}
          />
        </Stack>

        {/* Validation warning */}
        {validationError !== null && hasInteracted && (
          <Alert severity='warning'>{validationError}</Alert>
        )}
      </Stack>
    </StepContainer>
  );
}
